<template>
    <div>
        <div class="mb-6">
            <div class="flex justify-between items-center mb-4">
                <h2 class="font-header text-[3rem] font-bold text-primary">Comprehensive Analytics</h2>
                <button @click="() => loadAnalytics()"
                    class="px-4 py-2 bg-green-700 text-white rounded-md font-body text-body hover:bg-green-800 transition-colors"
                    :disabled="loading">
                    {{ loading ? 'Loading...' : 'Refresh' }}
                </button>
            </div>
            <div class="flex flex-wrap gap-3 items-center mb-4">
                <button v-for="period in timePeriods" :key="period.value" @click="setTimePeriod(period.value)" :class="[
                    'px-4 py-2 rounded-md font-body text-body transition-colors',
                    isDateRangeMatch(period.value)
                        ? 'bg-green-700 text-white'
                        : 'bg-white border border-creme-300 text-gray-700 hover:bg-creme-50'
                ]">
                    {{ period.label }}
                </button>
            </div>
            <div class="flex gap-4 items-center">
                <div>
                    <label class="block font-body text-body text-gray-600 mb-1">Start Date</label>
                    <input type="date" v-model="startDate" :max="endDate || undefined"
                        class="px-4 py-2 border border-creme-300 rounded-md font-body text-body"
                        @change="validateAndLoadAnalytics" />
                </div>
                <span class="font-body text-body text-gray-600 mt-6">to</span>
                <div>
                    <label class="block font-body text-body text-gray-600 mb-1">End Date</label>
                    <input type="date" v-model="endDate" :min="startDate || undefined"
                        class="px-4 py-2 border border-creme-300 rounded-md font-body text-body"
                        @change="validateAndLoadAnalytics" />
                </div>
            </div>
            <p v-if="dateRangeError" class="text-red-600 font-body text-body mt-2">{{ dateRangeError }}</p>
        </div>

        <div v-if="loading" class="text-center py-12">
            <Loader size="large" />
        </div>

        <div v-else-if="analytics">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-6">
                <div class="bg-white p-6 rounded-lg shadow-md">
                    <div class="flex flex-row items-center justify-between mb-2">
                        <span class="font-body text-[1.125rem] font-normal text-gray-700 m-0 leading-[1.75]">
                            Total Books Purchased
                        </span>
                        <!-- Removed period label as requested -->
                    </div>
                    <div class="flex flex-row items-center justify-between">
                        <span class="font-body text-[1.125rem] font-normal text-green-700 truncate leading-[1.75]">
                            {{ analytics.total_books_purchased || 0 }}
                        </span>
                    </div>
                </div>
                <div class="bg-white p-6 rounded-lg shadow-md">
                    <div class="flex flex-row items-center justify-between mb-2">
                        <span class="font-body text-[1.125rem] font-normal text-gray-700 m-0 leading-[1.75]">
                            Accounts Created
                        </span>
                        <!-- Removed period label as requested -->
                    </div>
                    <div class="flex flex-row items-center justify-between">
                        <span class="font-body text-[1.125rem] font-normal text-green-700 truncate leading-[1.75]">
                            {{ analytics.accounts_created || 0 }}
                        </span>
                    </div>
                </div>
                <div class="bg-white p-6 rounded-lg shadow-md">
                    <div class="flex flex-row items-center justify-between mb-2">
                        <span class="font-body text-[1.125rem] font-normal text-gray-700 m-0 leading-[1.75]">
                            Newsletter Subscriptions
                        </span>
                        <!-- Removed period label as requested -->
                    </div>
                    <div class="flex flex-row items-center justify-between">
                        <span class="font-body text-[1.125rem] font-normal text-green-700 truncate leading-[1.75]">
                            {{ analytics.newsletter_subscriptions || 0 }}
                        </span>
                    </div>
                </div>
                <div class="bg-white p-6 rounded-lg shadow-md">
                    <div class="flex flex-row items-center justify-between mb-2">
                        <span class="font-body text-[1.125rem] font-normal text-gray-700 m-0 leading-[1.75]">
                            Total Daily Visits
                        </span>
                        <!-- Removed period label as requested -->
                    </div>
                    <div class="flex flex-row items-center justify-between">
                        <span class="font-body text-[1.125rem] font-normal text-green-700 truncate leading-[1.75]">
                            {{ totalDailyVisits }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div class="bg-white p-6 rounded-lg shadow-md flex flex-col justify-between h-full">
                    <div class="flex flex-row items-center justify-between mb-2">
                        <h3 class="font-header text-[1.5rem] font-bold text-gray-600 m-0">
                            Total Revenue from Books
                        </h3>
                    </div>
                    <div class="flex flex-row items-center justify-between">
                        <span class="font-body text-[1.125rem] font-normal text-green-700 truncate leading-[1.75]">
                            ${{ formatCurrency(analytics.total_revenue_books) }}
                        </span>
                    </div>
                </div>
                <div class="bg-white p-6 rounded-lg shadow-md flex flex-col justify-between h-full">
                    <div class="flex flex-row items-center justify-between mb-2">
                        <h3 class="font-header text-[1.5rem] font-bold text-gray-600 m-0">
                            Total Revenue from Events
                        </h3>
                    </div>
                    <div class="flex flex-row items-center justify-between">
                        <span class="font-body text-[1.125rem] font-normal text-green-700 truncate leading-[1.75]">
                            ${{ formatCurrency(analytics.total_revenue_events) }}
                        </span>
                    </div>
                </div>
                <div class="bg-white p-6 rounded-lg shadow-md flex flex-col justify-between h-full">
                    <div class="flex flex-row items-center justify-between mb-2">
                        <h3 class="font-header text-[1.5rem] font-bold text-gray-600 m-0">
                            Total Revenue from Donations
                        </h3>
                    </div>
                    <div class="flex flex-row items-center justify-between">
                        <span class="font-body text-[1.125rem] font-normal text-green-700 truncate leading-[1.75]">
                            ${{ formatCurrency(analytics.total_revenue_donations) }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div class="bg-white rounded-lg shadow-md p-6">
                    <h3 class="text-xl font-marketing font-semibold mb-4">Books Purchased by Format</h3>
                    <div v-if="analytics.books_purchased_by_format && analytics.books_purchased_by_format.length > 0"
                        class="space-y-2">
                        <div v-for="item in analytics.books_purchased_by_format" :key="item.format"
                            class="flex justify-between items-center p-3 bg-creme-50 rounded-md">
                            <span class="font-marketing capitalize">{{ item.format }}</span>
                            <span class="font-marketing font-semibold text-green-700">{{ item.count }} books</span>
                        </div>
                    </div>
                    <p v-else class="text-gray-500 font-marketing text-center py-4">
                        No book purchases in this period
                    </p>
                </div>

                <div class="bg-white rounded-lg shadow-md p-6">
                    <h3 class="text-xl font-marketing font-semibold mb-4">Revenue by Book Format</h3>
                    <div v-if="analytics.revenue_by_book_format && analytics.revenue_by_book_format.length > 0"
                        class="space-y-2">
                        <div v-for="item in analytics.revenue_by_book_format" :key="item.format"
                            class="flex justify-between items-center p-3 bg-creme-50 rounded-md">
                            <span class="font-marketing capitalize">{{ item.format }}</span>
                            <span class="font-marketing font-semibold text-green-700">${{ formatCurrency(item.revenue)
                                }}</span>
                        </div>
                    </div>
                    <p v-else class="text-gray-500 font-marketing text-center py-4">
                        No revenue from books in this period
                    </p>
                </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div class="bg-white rounded-lg shadow-md p-6">
                    <h3 class="text-xl font-marketing font-semibold mb-4">Revenue by Event Type</h3>
                    <div v-if="analytics.revenue_by_event_type && analytics.revenue_by_event_type.length > 0"
                        class="space-y-2">
                        <div v-for="item in analytics.revenue_by_event_type" :key="item.event_type"
                            class="flex justify-between items-center p-3 bg-creme-50 rounded-md">
                            <span class="font-marketing capitalize">{{ item.event_type }}</span>
                            <span class="font-marketing font-semibold text-green-700">${{ formatCurrency(item.revenue)
                                }}</span>
                        </div>
                    </div>
                    <p v-else class="text-gray-500 font-marketing text-center py-4">
                        No revenue from events in this period
                    </p>
                </div>

                <div class="bg-white rounded-lg shadow-md p-6">
                    <h3 class="text-xl font-marketing font-semibold mb-4">Purchaser Locations</h3>
                    <div v-if="analytics.purchaser_locations && analytics.purchaser_locations.length > 0"
                        class="space-y-2">
                        <div v-for="location in analytics.purchaser_locations" :key="location.location"
                            class="flex justify-between items-center p-3 bg-creme-50 rounded-md">
                            <span class="font-marketing">{{ location.location }}</span>
                            <span class="font-marketing font-semibold text-green-700">{{ location.count }}
                                purchases</span>
                        </div>
                    </div>
                    <p v-else class="text-gray-500 font-marketing text-center py-4">
                        No location data available
                    </p>
                </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div class="bg-white rounded-lg shadow-md overflow-hidden">
                    <div class="p-6 border-b border-creme-200">
                        <h3 class="text-xl font-marketing font-semibold">Daily Visits</h3>
                    </div>
                    <div v-if="dailyVisitsLoading" class="p-8 text-center">
                        <Loader />
                    </div>
                    <div v-else-if="dailyVisitsData && dailyVisitsData.data && dailyVisitsData.data.length > 0">
                        <div class="space-y-2 max-h-96 overflow-y-auto">
                            <div v-for="visit in dailyVisitsData.data" :key="visit.date"
                                class="flex justify-between items-center p-3 bg-creme-50 rounded-md mx-4 my-2">
                                <span class="font-marketing">{{ formatDate(visit.date) }}</span>
                                <span class="font-marketing font-semibold text-green-700">{{ visit.count }}
                                    visits</span>
                            </div>
                        </div>
                        <div v-if="dailyVisitsPagination && dailyVisitsPagination.total_pages > 1"
                            class="p-4 border-t border-creme-200 flex justify-between items-center">
                            <div class="text-sm font-marketing text-gray-600">
                                Page {{ dailyVisitsPagination.page }} of {{ dailyVisitsPagination.total_pages }}
                                ({{ dailyVisitsPagination.total }} total)
                            </div>
                            <div class="flex gap-2">
                                <button @click="loadDailyVisits(dailyVisitsPagination.page - 1)"
                                    :disabled="!dailyVisitsPagination.prev_page || dailyVisitsLoading"
                                    class="px-4 py-2 bg-white border border-creme-300 rounded-md font-marketing text-gray-700 hover:bg-creme-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                    Previous
                                </button>
                                <button @click="loadDailyVisits(dailyVisitsPagination.page + 1)"
                                    :disabled="!dailyVisitsPagination.next_page || dailyVisitsLoading"
                                    class="px-4 py-2 bg-white border border-creme-300 rounded-md font-marketing text-gray-700 hover:bg-creme-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                    <p v-else class="text-gray-500 font-marketing text-center py-8">
                        No daily visit data available
                    </p>
                </div>

                <div class="bg-white rounded-lg shadow-md overflow-hidden">
                    <div class="p-6 border-b border-creme-200">
                        <h3 class="text-xl font-marketing font-semibold">Top Book Views</h3>
                    </div>
                    <div v-if="topBookViewsLoading" class="p-8 text-center">
                        <Loader />
                    </div>
                    <div v-else-if="topBookViewsData && topBookViewsData.data && topBookViewsData.data.length > 0">
                        <div class="divide-y divide-creme-200 max-h-96 overflow-y-auto">
                            <div v-for="book in topBookViewsData.data" :key="book.book_id"
                                class="p-4 hover:bg-creme-50 transition-colors">
                                <div class="flex justify-between items-start">
                                    <div class="flex-1">
                                        <p class="font-marketing font-semibold text-primary" data-no-translate>{{
                                            book.book_title }}</p>
                                        <p class="font-marketing text-sm text-gray-600 mt-1">Book ID: {{ book.book_id }}
                                        </p>
                                    </div>
                                    <span class="font-marketing font-bold text-green-700 ml-4">{{ book.count }}
                                        views</span>
                                </div>
                            </div>
                        </div>
                        <div v-if="topBookViewsPagination && topBookViewsPagination.total_pages > 1"
                            class="p-4 border-t border-creme-200 flex justify-between items-center">
                            <div class="text-sm font-marketing text-gray-600">
                                Page {{ topBookViewsPagination.page }} of {{ topBookViewsPagination.total_pages }}
                                ({{ topBookViewsPagination.total }} total)
                            </div>
                            <div class="flex gap-2">
                                <button @click="loadTopBookViews(topBookViewsPagination.page - 1)"
                                    :disabled="!topBookViewsPagination.prev_page || topBookViewsLoading"
                                    class="px-4 py-2 bg-white border border-creme-300 rounded-md font-marketing text-gray-700 hover:bg-creme-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                    Previous
                                </button>
                                <button @click="loadTopBookViews(topBookViewsPagination.page + 1)"
                                    :disabled="!topBookViewsPagination.next_page || topBookViewsLoading"
                                    class="px-4 py-2 bg-white border border-creme-300 rounded-md font-marketing text-gray-700 hover:bg-creme-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                    <p v-else class="text-gray-500 font-marketing text-center py-8">
                        No book view data available
                    </p>
                </div>
            </div>

            <div class="bg-white rounded-lg shadow-md overflow-hidden">
                <div class="p-6 border-b border-creme-200">
                    <h3 class="text-xl font-marketing font-semibold">Top Searches</h3>
                </div>
                <div v-if="topSearchesLoading" class="p-8 text-center">
                    <Loader />
                </div>
                <div v-else-if="topSearchesData && topSearchesData.data && topSearchesData.data.length > 0">
                    <div class="divide-y divide-creme-200 max-h-96 overflow-y-auto">
                        <div v-for="(search, index) in topSearchesData.data" :key="index"
                            class="p-4 hover:bg-creme-50 transition-colors">
                            <div class="flex justify-between items-center">
                                <span class="font-marketing flex-1">{{ search.search_query }}</span>
                                <span class="font-marketing font-bold text-green-700 ml-4">{{ search.count }} {{
                                    search.count === 1 ? 'search' : 'searches' }}</span>
                            </div>
                        </div>
                    </div>
                    <div v-if="topSearchesPagination && topSearchesPagination.total_pages > 1"
                        class="p-4 border-t border-creme-200 flex justify-between items-center">
                        <div class="text-sm font-marketing text-gray-600">
                            Page {{ topSearchesPagination.page }} of {{ topSearchesPagination.total_pages }}
                            ({{ topSearchesPagination.total }} total)
                        </div>
                        <div class="flex gap-2">
                            <button @click="loadTopSearches(topSearchesPagination.page - 1)"
                                :disabled="!topSearchesPagination.prev_page || topSearchesLoading"
                                class="px-4 py-2 bg-white border border-creme-300 rounded-md font-marketing text-gray-700 hover:bg-creme-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                Previous
                            </button>
                            <button @click="loadTopSearches(topSearchesPagination.page + 1)"
                                :disabled="!topSearchesPagination.next_page || topSearchesLoading"
                                class="px-4 py-2 bg-white border border-creme-300 rounded-md font-marketing text-gray-700 hover:bg-creme-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                <p v-else class="text-gray-500 font-marketing text-center py-8">
                    No search data available
                </p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { adminService } from '../../services/admin'
import Loader from '../Loader.vue'
import { useLoadingState } from '../../composables/useLoadingState'
import { formatDateShort, getESTToday } from '../../utils/date'

const { loading, setLoading } = useLoadingState(false)
const analytics = ref(null)
const topSearchesData = ref(null)
const topSearchesPagination = ref(null)
const topSearchesLoading = ref(false)
const topBookViewsData = ref(null)
const topBookViewsPagination = ref(null)
const topBookViewsLoading = ref(false)
const dailyVisitsData = ref(null)
const dailyVisitsPagination = ref(null)
const dailyVisitsLoading = ref(false)

const startDate = ref('')
const endDate = ref('')
const selectedPeriod = ref(null)
const dateRangeError = ref('')
const isSettingDates = ref(false)

const timePeriods = [
    { label: 'Today', value: 'today' },
    { label: 'Last 7 Days', value: '7days' },
    { label: 'Last 30 Days', value: '30days' },
    { label: 'Last 90 Days', value: '90days' },
    { label: 'This Year', value: 'year' },
    { label: 'All Time', value: 'all' }
]

const totalDailyVisits = computed(() => {
    if (!analytics.value?.total_daily_visits) return 0
    return analytics.value.total_daily_visits.reduce((sum, visit) => sum + visit.count, 0)
})


const formatDate = formatDateShort

const formatCurrency = (value) => {
    if (!value) return '0.00'
    const num = typeof value === 'string' ? parseFloat(value) : value
    return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const formatDateForAPI = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const setTimePeriod = (period) => {
    isSettingDates.value = true
    selectedPeriod.value = period
    const today = getESTToday()

    let start = new Date(today)

    switch (period) {
        case 'today':
            start = new Date(today)
            break
        case '7days':
            start.setDate(today.getDate() - 7)
            break
        case '30days':
            start.setDate(today.getDate() - 30)
            break
        case '90days':
            start.setDate(today.getDate() - 90)
            break
        case 'year':
            start = new Date(today.getFullYear(), 0, 1)
            break
        case 'all':
            start = null
            break
    }

    endDate.value = formatDateForAPI(today)
    startDate.value = start ? formatDateForAPI(start) : ''

    // Use nextTick to ensure dates are set before loading
    setTimeout(() => {
        isSettingDates.value = false
        loadAnalytics()
    }, 0)
}

const isDateRangeMatch = (period) => {
    const today = getESTToday()
    let expectedStart = new Date(today)

    switch (period) {
        case 'today':
            expectedStart = new Date(today)
            break
        case '7days':
            expectedStart.setDate(today.getDate() - 7)
            break
        case '30days':
            expectedStart.setDate(today.getDate() - 30)
            break
        case '90days':
            expectedStart.setDate(today.getDate() - 90)
            break
        case 'year':
            expectedStart = new Date(today.getFullYear(), 0, 1)
            break
        case 'all': {
            // For all time, only end date should be today
            const expectedEnd = formatDateForAPI(today)
            return startDate.value === '' && endDate.value === expectedEnd
        }
    }

    const expectedStartStr = formatDateForAPI(expectedStart)
    const expectedEnd = formatDateForAPI(today)

    return startDate.value === expectedStartStr && endDate.value === expectedEnd
}

const validateAndLoadAnalytics = () => {
    // Don't trigger load if dates are being set programmatically
    if (isSettingDates.value) {
        return
    }

    dateRangeError.value = ''

    if (startDate.value && endDate.value && startDate.value > endDate.value) {
        dateRangeError.value = 'Start date must be before or equal to end date'
        return
    }

    // Reset selected period since user is using custom dates
    selectedPeriod.value = null

    loadAnalytics()
}

const loadAnalytics = async () => {
    setLoading(true)
    dateRangeError.value = ''

    try {
        const params = {}
        if (startDate.value && startDate.value.trim()) {
            params.start_date = startDate.value.trim()
        }
        if (endDate.value && endDate.value.trim()) {
            params.end_date = endDate.value.trim()
        }
        params.limit = 100

        const data = await adminService.getComprehensiveAnalytics(params)
        analytics.value = data || null

        loadTopSearches(1)
        loadTopBookViews(1)
        loadDailyVisits(1)
    } catch (error) {
        console.error('Failed to load comprehensive analytics:', error)
        const errorMessage = error.response?.data?.error || error.message || 'Unknown error'
        if (errorMessage.includes('start_date')) {
            dateRangeError.value = errorMessage
        } else {
            console.error('Error details:', error.response?.data || error.message)
        }
        analytics.value = null
    } finally {
        setLoading(false)
    }
}

const loadTopSearches = async (page = 1) => {
    topSearchesLoading.value = true

    try {
        const params = {
            page,
            limit: 20
        }
        if (startDate.value && startDate.value.trim()) {
            params.start_date = startDate.value.trim()
        }
        if (endDate.value && endDate.value.trim()) {
            params.end_date = endDate.value.trim()
        }

        const data = await adminService.getTopSearches(params)
        topSearchesData.value = data || null
        topSearchesPagination.value = data?.pagination || null
    } catch (error) {
        console.error('Failed to load top searches:', error)
        topSearchesData.value = null
        topSearchesPagination.value = null
    } finally {
        topSearchesLoading.value = false
    }
}

const loadTopBookViews = async (page = 1) => {
    topBookViewsLoading.value = true

    try {
        const params = {
            page,
            limit: 20
        }
        if (startDate.value && startDate.value.trim()) {
            params.start_date = startDate.value.trim()
        }
        if (endDate.value && endDate.value.trim()) {
            params.end_date = endDate.value.trim()
        }

        const data = await adminService.getTopBookViews(params)
        topBookViewsData.value = data || null
        topBookViewsPagination.value = data?.pagination || null
    } catch (error) {
        console.error('Failed to load top book views:', error)
        topBookViewsData.value = null
        topBookViewsPagination.value = null
    } finally {
        topBookViewsLoading.value = false
    }
}

const loadDailyVisits = async (page = 1) => {
    dailyVisitsLoading.value = true

    try {
        const params = {
            page,
            limit: 20
        }
        if (startDate.value && startDate.value.trim()) {
            params.start_date = startDate.value.trim()
        }
        if (endDate.value && endDate.value.trim()) {
            params.end_date = endDate.value.trim()
        }

        const data = await adminService.getDailyVisits(params)
        dailyVisitsData.value = data || null
        dailyVisitsPagination.value = data?.pagination || null
    } catch (error) {
        console.error('Failed to load daily visits:', error)
        dailyVisitsData.value = null
        dailyVisitsPagination.value = null
    } finally {
        dailyVisitsLoading.value = false
    }
}

onMounted(() => {
    setTimePeriod('30days')
})
</script>
